"use client";

import React from 'react';
import { useCompare } from '@/contexts/CompareContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faStar, faStarHalfAlt } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarEmpty } from '@fortawesome/free-regular-svg-icons';
import { formatCurrency } from '@/lib/format';
import Link from 'next/link';
import { slugify } from '@/lib/slugify';
import { getFullUrl } from '@/lib/utils';
import { ENV } from '@/core/config/env';
import Image from 'next/image';

export default function CompareModal() {
    const { compareProducts, isCompareOpen, setCompareOpen, removeFromCompare, clearCompare } = useCompare();

    if (!isCompareOpen || compareProducts.length === 0) {
        return null;
    }

    const renderStars = (rating: number) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;

        for (let i = 0; i < fullStars; i++) {
            stars.push(
                <FontAwesomeIcon key={`full-${i}`} icon={faStar} className="text-yellow-400" />
            );
        }

        if (hasHalfStar) {
            stars.push(
                <FontAwesomeIcon key="half" icon={faStarHalfAlt} className="text-yellow-400" />
            );
        }

        const emptyStars = 5 - Math.ceil(rating);
        for (let i = 0; i < emptyStars; i++) {
            stars.push(
                <FontAwesomeIcon key={`empty-${i}`} icon={faStarEmpty} className="text-gray-300" />
            );
        }

        return stars;
    };

    return (
        <div className="fixed inset-0 z-[1300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="relative w-full max-w-7xl bg-white rounded-lg shadow-2xl my-8 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-lg shadow-md flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-white">Compare Products ({compareProducts.length})</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={clearCompare}
                                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                Clear All
                            </button>
                            <button
                                onClick={() => setCompareOpen(false)}
                                className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                                aria-label="Close comparison"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Comparison Table */}
                <div className="overflow-auto flex-1">
                    <table className="w-full">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <th className="sticky left-0 bg-gray-50 px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b-2 border-gray-200 z-20">
                                    Features
                                </th>
                                {compareProducts.map((product) => (
                                    <th
                                        key={product.productID}
                                        className="relative px-6 py-4 text-center border-b-2 border-gray-200 min-w-[250px]"
                                    >
                                        <div className="font-semibold text-gray-900 text-sm mb-1 pr-8 line-clamp-2">
                                            {product.productName}
                                        </div>
                                        <button
                                            onClick={() => removeFromCompare(product.productID)}
                                            className="absolute top-2 right-2 p-1 bg-red-100 hover:bg-red-200 text-red-600 rounded-full transition-colors"
                                            aria-label="Remove from comparison"
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="text-sm" />
                                        </button>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {/* Product Image */}
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">
                                    Product Image
                                </td>
                                {compareProducts.map((product) => (
                                    <td key={product.productID} className="px-6 py-4 text-center">
                                        <Link href={getFullUrl(`car-parts/${product.productID}/${slugify(product.productName ?? '')}`)}>
                                            <div className="relative w-40 h-40 mx-auto">
                                                <Image
                                                    src={product.productImage ? `${ENV.IMAGE_URL}${product.productImage}` : '/images/no-image.webp'}
                                                    alt={product.productName}
                                                    fill
                                                    className="object-contain rounded-lg shadow-sm hover:shadow-md transition-shadow"
                                                />
                                            </div>
                                        </Link>
                                    </td>
                                ))}
                            </tr>

                            {/* Product Name */}
                            <tr className="bg-gray-50">
                                <td className="sticky left-0 bg-gray-50 px-6 py-4 font-medium text-gray-900">
                                    Product Name
                                </td>
                                {compareProducts.map((product) => (
                                    <td key={product.productID} className="px-6 py-4">
                                        <Link
                                            href={getFullUrl(`car-parts/${product.productID}/${slugify(product.productName ?? '')}`)}
                                            className="font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                                        >
                                            {product.productName}
                                        </Link>
                                    </td>
                                ))}
                            </tr>

                            {/* Price */}
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">
                                    Price
                                </td>
                                {compareProducts.map((product) => (
                                    <td key={product.productID} className="px-6 py-4">
                                        <span className="text-2xl font-bold text-green-600">
                                            {formatCurrency(product.unitCost)}
                                        </span>
                                    </td>
                                ))}
                            </tr>

                            {/* Rating */}
                            <tr className="bg-gray-50">
                                <td className="sticky left-0 bg-gray-50 px-6 py-4 font-medium text-gray-900">
                                    Rating
                                </td>
                                {compareProducts.map((product) => (
                                    <td key={product.productID} className="px-6 py-4">
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="flex gap-1">
                                                {renderStars(product.ratingScore || 0)}
                                            </div>
                                            <span className="text-sm font-semibold text-gray-700">
                                                {(product.ratingScore || 0).toFixed(1)}
                                            </span>
                                        </div>
                                    </td>
                                ))}
                            </tr>

                            {/* Sales Type */}
                            <tr className="bg-white">
                                <td className="sticky left-0 bg-white px-6 py-4 font-medium text-gray-900">
                                    Sales Type
                                </td>
                                {compareProducts.map((product) => (
                                    <td key={product.productID} className="px-6 py-4">
                                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                            product.salesTypeId === 1 ? 'bg-green-100 text-green-800' :
                                            product.salesTypeId === 2 ? 'bg-blue-100 text-blue-800' :
                                            'bg-purple-100 text-purple-800'
                                        }`}>
                                            {product.salesTypeId === 1 ? 'Purchase' :
                                             product.salesTypeId === 2 ? 'Bid' : 'Hire'}
                                        </span>
                                    </td>
                                ))}
                            </tr>

                            {/* Availability */}
                            <tr className="bg-gray-50">
                                <td className="sticky left-0 bg-gray-50 px-6 py-4 font-medium text-gray-900">
                                    Availability
                                </td>
                                {compareProducts.map((product) => (
                                    <td key={product.productID} className="px-6 py-4">
                                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                            product.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            {product.isActive ? 'In Stock' : 'Out of Stock'}
                                        </span>
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t border-gray-200 flex-shrink-0">
                    <p className="text-sm text-gray-600 text-center">
                        Select up to 4 products to compare their features side by side
                    </p>
                </div>
            </div>
        </div>
    );
}
